import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckSquare, Square, Trash2, Loader2, X, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";

const STAGES = [
  { key: "new", label: "New" },
  { key: "ai_reviewing", label: "AI Reviewing" },
  { key: "interested", label: "Interested" },
  { key: "estimating", label: "Estimating" },
  { key: "in_progress", label: "In Progress" },
  { key: "needs_review", label: "Needs Review" },
  { key: "submitted", label: "Submitted" },
  { key: "awarded", label: "Awarded" },
  { key: "lost", label: "Lost" },
  { key: "follow_up", label: "Follow-Up" },
];

const stageColor = {
  new: "border-slate-500 text-slate-400",
  ai_reviewing: "border-cyan-400 text-cyan-400",
  interested: "border-blue-400 text-blue-400",
  estimating: "border-amber-400 text-amber-400",
  in_progress: "border-amber-400 text-amber-400",
  needs_review: "border-cyan-400 text-cyan-400",
  submitted: "border-purple-400 text-purple-400",
  awarded: "border-green-400 text-green-400",
  lost: "border-red-400 text-red-400",
  follow_up: "border-slate-400 text-slate-400",
};

// An opportunity is "expired" when its response_deadline is in the past.
// Deadlines missing or unparseable are treated as NOT expired (safer default —
// we don't want to accidentally purge cards that just lack a deadline field).
function isExpired(o) {
  const d = o?.response_deadline;
  if (!d) return false;
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return false;
  return t < Date.now();
}

export default function Pipeline() {
  const [opps, setOpps] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = () => api.get("/opportunities").then((r) => setOpps(r.data));
  useEffect(() => { load(); }, []);

  const byStage = (stage) => opps.filter((o) => (o.pipeline_stage || "new") === stage);

  // Count of expired items — surfaces in the "Select expired" quick action.
  const expiredCount = useMemo(() => opps.filter(isExpired).length, [opps]);

  const toggleOne = (nid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(nid)) next.delete(nid); else next.add(nid);
      return next;
    });
  };

  const selectExpired = () => {
    setSelectMode(true);
    setSelected(new Set(opps.filter(isExpired).map((o) => o.notice_id)));
    if (expiredCount === 0) toast("No expired opportunities to select.");
  };

  const clearSelection = () => setSelected(new Set());

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Set());
  };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    setDeleting(true);
    try {
      const r = await api.post("/opportunities/bulk/archive", { notice_ids: Array.from(selected) });
      toast.success(`Removed ${r.data.deleted} opportunit${r.data.deleted === 1 ? "y" : "ies"} from the pipeline`);
      setConfirmOpen(false);
      exitSelectMode();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-8 stagger-in" data-testid="pipeline-page">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label text-cyan-400 mb-2">// CRM · PIPELINE</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Deal flow</h1>
          <p className="text-slate-400 mt-2">Kanban view of every opportunity through the pipeline.</p>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          {!selectMode ? (
            <>
              <button
                onClick={selectExpired}
                disabled={expiredCount === 0}
                className="border border-amber-400/40 text-amber-400 hover:border-amber-400 px-3 py-2 mono-label inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="select-expired-btn"
                title={expiredCount === 0 ? "No opportunities past their deadline" : "Bulk-select every opportunity past its response deadline"}
              >
                <CalendarClock className="w-3.5 h-3.5" />
                SELECT EXPIRED{expiredCount > 0 && ` (${expiredCount})`}
              </button>
              <button
                onClick={() => setSelectMode(true)}
                className="border border-white/10 hover:border-cyan-400/60 text-slate-300 hover:text-cyan-400 px-3 py-2 mono-label inline-flex items-center gap-2"
                data-testid="enter-select-mode-btn"
              >
                <CheckSquare className="w-3.5 h-3.5" />
                SELECT
              </button>
            </>
          ) : (
            <>
              <div
                className="border border-cyan-400/60 text-cyan-400 px-3 py-2 mono-label"
                data-testid="selected-count"
              >
                {selected.size} SELECTED
              </div>
              {selected.size > 0 && (
                <button
                  onClick={clearSelection}
                  className="border border-white/10 hover:border-white/30 text-slate-400 hover:text-slate-200 px-3 py-2 mono-label inline-flex items-center gap-2"
                  data-testid="clear-selection-btn"
                >
                  CLEAR
                </button>
              )}
              <button
                onClick={() => setConfirmOpen(true)}
                disabled={selected.size === 0}
                className="border border-red-500/40 text-red-400 hover:border-red-500 hover:bg-red-500/10 px-3 py-2 mono-label inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
                DELETE SELECTED
              </button>
              <button
                onClick={exitSelectMode}
                className="border border-white/10 hover:border-white/30 text-slate-400 hover:text-slate-200 px-3 py-2 mono-label inline-flex items-center gap-2"
                data-testid="exit-select-mode-btn"
              >
                <X className="w-3.5 h-3.5" />
                DONE
              </button>
            </>
          )}
        </div>
      </div>

      {/* Sticky helper strip when in select mode */}
      {selectMode && (
        <div className="mb-4 bg-[#0D1424] border border-cyan-400/30 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="select-mode-banner">
          <div className="text-slate-300 text-sm">
            <span className="mono-label text-cyan-400 mr-2">SELECT MODE</span>
            Click any card to toggle. Deleted opportunities are permanently removed from your pipeline.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={selectExpired}
              className="border border-amber-400/40 text-amber-400 hover:border-amber-400 px-3 py-1.5 mono-label inline-flex items-center gap-2 text-xs"
              data-testid="banner-select-expired-btn"
            >
              <CalendarClock className="w-3 h-3" />
              +EXPIRED ({expiredCount})
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <div className="flex gap-4 min-w-max pb-4">
          {STAGES.map((s) => {
            const items = byStage(s.key);
            return (
              <div key={s.key} className="w-72 shrink-0" data-testid={`col-${s.key}`}>
                <div className={`p-3 border ${stageColor[s.key]} mono-label flex items-center justify-between bg-[#0D1424]`}>
                  <span>{s.label}</span>
                  <span>{items.length}</span>
                </div>
                <div className="mt-2 space-y-2 min-h-[200px]">
                  {items.map((o) => (
                    <OppCard
                      key={o.notice_id}
                      o={o}
                      selectMode={selectMode}
                      selected={selected.has(o.notice_id)}
                      onToggle={() => toggleOne(o.notice_id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Confirm delete modal */}
      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="confirm-delete-modal">
          <div className="bg-[#0D1424] border border-red-500/40 max-w-md w-full p-6">
            <div className="mono-label text-red-400 mb-2">// PERMANENT ACTION</div>
            <h3 className="font-display font-bold text-xl mb-3">
              Delete {selected.size} opportunit{selected.size === 1 ? "y" : "ies"}?
            </h3>
            <p className="text-slate-400 text-sm mb-6">
              These opportunities will be removed from your pipeline permanently. Related proposals, takeoffs and estimates stay in their own workspaces — this only deletes the pipeline entry.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
                className="border border-white/10 hover:border-white/30 text-slate-300 px-4 py-2 mono-label inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="confirm-delete-cancel"
              >
                CANCEL
              </button>
              <button
                onClick={deleteSelected}
                disabled={deleting}
                className="bg-red-500/20 border border-red-500 text-red-300 hover:bg-red-500/30 px-4 py-2 mono-label inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                DELETE {selected.size}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OppCard({ o, selectMode, selected, onToggle }) {
  const expired = isExpired(o);
  const cardClass = `block bg-[#0D1424] border p-3 transition-colors ${
    selected
      ? "border-cyan-400"
      : expired
        ? "border-amber-500/40 hover:border-amber-500"
        : "border-white/10 hover:border-cyan-400/40"
  }`;

  const inner = (
    <>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selectMode && (
            selected
              ? <CheckSquare className="w-4 h-4 text-cyan-400 shrink-0" />
              : <Square className="w-4 h-4 text-slate-500 shrink-0" />
          )}
          <div className="mono-label text-slate-500 truncate flex-1">{o.agency}</div>
        </div>
        <span
          className={`font-display font-bold text-lg ${
            o.match_score >= 85 ? "text-cyan-400" : o.match_score >= 70 ? "text-amber-400" : "text-slate-400"
          }`}
        >
          {o.match_score}
        </span>
      </div>
      <div className="text-sm text-white line-clamp-2">{o.title}</div>
      {o.response_deadline && (
        <div className={`mono-label mt-2 ${expired ? "text-amber-500" : "text-slate-500"}`}>
          {expired ? "EXPIRED · " : "DUE "}{new Date(o.response_deadline).toLocaleDateString()}
        </div>
      )}
    </>
  );

  if (selectMode) {
    return (
      <button
        type="button"
        onClick={onToggle}
        className={`${cardClass} text-left w-full cursor-pointer`}
        data-testid={`card-${o.notice_id}`}
        aria-pressed={selected}
      >
        {inner}
      </button>
    );
  }
  return (
    <Link to={`/opportunities/${o.notice_id}`} className={cardClass} data-testid={`card-${o.notice_id}`}>
      {inner}
    </Link>
  );
}
